import React from "react";
import { Link, useParams } from "react-router-dom";
import { MessageSquareText, SendHorizonal } from "lucide-react";
import { MisReportsView } from "../../components/MisReportsView";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel } from "../../components/FeatureGate";
import { PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { useAuth } from "../../context/AuthContext";

/** Admin MIS Reports — municipality-wide. The body lives in `MisReportsView` (shared with the zone-scoped
 *  Supervisor page from Phase 143); all historical data-testids are kept via the component defaults.
 *  Phase 152: "reports.mis_reports" (View only -> no PDF / Excel) and "reports.field_activity" (hidden when No access,
 *  View only -> no field-activity export) sub-features.
 *  Phase 221 / 227: dues notices are never automatic. Since Phase 227 every manual message (dues notices + the types the
 *  Super Admin set to Manual) is sent from ONE screen - Notifications → Send Messages - so this section only points there. */
export default function Reports() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const { auth } = useAuth() || {};
  const isAdmin = auth?.user?.role === "municipal_admin";
  if (access.loaded && !access.featureAllowed("reports", "mis_reports") && !access.featureAllowed("reports", "field_activity")) {
    return (
      <div>
        <PageHeader title="MIS Reports" subtitle="Download management reports as PDF or Excel" />
        <NoAccessPanel what="MIS Reports" testId="reports-no-access" />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <MisReportsView code={code} expandable
        canDownload={access.canWrite("reports", "mis_reports")}
        hideReportCards={!access.featureAllowed("reports", "mis_reports")}
        showFieldActivity={access.featureAllowed("reports", "field_activity")}
        canExportFieldActivity={access.canWrite("reports", "field_activity")}
        collectionsHref={(ap, a) => `/${code}/admin/collections?date_from=${ap?.dateFrom || ""}&date_to=${ap?.dateTo || ""}${a?.zoneId ? `&zone_id=${a.zoneId}` : ""}${a?.wardId ? `&ward_id=${a.wardId}` : ""}`} />
      {isAdmin && (
        <section id="dues-notices" className="space-y-2" data-testid="dues-notices-section">
          <h2 className="text-lg font-heading font-semibold flex items-center gap-2"><MessageSquareText className="h-5 w-5" /> Dues notices</h2>
          <Card className="shadow-sm">
            <CardContent className="p-4 flex items-start justify-between gap-4 flex-wrap">
              <div className="text-sm space-y-1 min-w-0">
                <p className="font-medium">Property Tax Due, Pending Payment and Final Notice are sent by hand from Notifications → Send Messages.</p>
                <p className="text-xs text-muted-foreground">Choose the notice, a Mohalla / Ward / Zone / the whole municipality or specific properties, the channels (SMS, WhatsApp, e-mail), preview the recipients (&quot;12 properties, 12 messages&quot;) and confirm. Properties notified recently are skipped by the municipality's skip-days rule.</p>
              </div>
              <Button asChild variant="outline" data-testid="open-send-messages-button">
                <Link to={`/${code}/admin/notifications#send`}><SendHorizonal className="h-4 w-4 mr-1.5" /> Open Send Messages</Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
